import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { PayTable } from "@lib/titan2-slingo/dist/game/view/abstract/display/PayTable";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { EventObject } from "@lib/titan2/dist/util/event/EventObject";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { gsap } from "gsap";
import { PixiPlugin } from "gsap/all";
import { MotionBlurFilter } from "pixi-filters";
import { GameEvent } from "../../../../controller/event/GameEvent";
import { GameSoundEvent } from "../../../../controller/event/GameSoundEvent";
import { ApiConstants } from "../../../../model/enum/ApiConstants";
import { GameMathUtil } from "../../../../utils/GameMathUtil";
import { GameSpineUtil } from "../../../../utils/GameSpineUtil";
import { GamePayTableCell } from "./Cells/GamePayTableCell";
import { PayTableSpineRegister } from "./PayTableSpineRegister";
gsap.registerPlugin(PixiPlugin);
/**
 * GamePayTable.ts
 */
export class GamePayTable extends PayTable {
    constructor(factory, landscapeProperties, portraitProperties) {
        super(factory, landscapeProperties, portraitProperties);
        this.landscapeProperties = landscapeProperties;
        this.cellIndex = this.numberOfPatternsToCell(0);
        this.handleFontSizingDynamic();
    }
    create() {
        this.createModel();
        this.animation = TitanSpine.createSpine(this, this.landscapeProperties.animationProperties, this.portraitProperties?.animationProperties);
        this.createCells();
        this.createBackToGameButton();
        this.setupClickEvents();
    }
    createCells() {
        this.cells = [];
        this.customCells = [];
        // Create texts for each number of patterns up to the max cells.
        for (let i = 0; i < ApiConstants.CELL_COUNT; i++) {
            const cellLandscape = this.landscapeProperties.cells[i];
            const cellPortrait = this.portraitProperties?.cells[i];
            const cell = new GamePayTableCell(this.factory, cellLandscape, cellPortrait, i);
            this.customCells.push(cell);
        }
        this.updateCellOrientation();
    }
    setupClickEvents() {
        for (const spine of GameSpineUtil.orientations(this.animation)) {
            for (let index = 0; index < ApiConstants.CELL_COUNT; ++index) {
                const stateSlots = ["inactive", "active", "passed"]
                    .map(id => GameSpineUtil.getSlot(spine, `PORTRAIT/cell-${id}_${index}`))
                    .filter(Boolean);
                const textSlots = ["label", "value"]
                    .map(id => GameSpineUtil.getSlot(spine, `dynamic-${id}-${index}`))
                    .filter(Boolean);
                // Make all the state slots interactive.
                for (const slot of stateSlots) {
                    slot.interactive = true;
                    slot.on("pointerdown", () => {
                        this.dispatchGame(new EventObject(GameSoundEvent.LADDER_NOTE.getType(), { note: index + 1 }));
                        // Blur the state slots.
                        for (const slot of stateSlots) {
                            const motion = (slot.filters?.[0] ?? new MotionBlurFilter());
                            slot.filters = [motion];
                            gsap.fromTo(motion.velocity, { x: 30 }, { x: 0, duration: 0.5, onComplete: () => slot.filters = [], overwrite: true });
                        }
                        // Blur the text slots slightly less.
                        for (const slot of textSlots) {
                            const motion = (slot.filters?.[0] ?? new MotionBlurFilter());
                            slot.filters = [motion];
                            gsap.fromTo(motion.velocity, { x: 10 }, { x: 0, duration: 0.5, onComplete: () => slot.filters = [], overwrite: true });
                        }
                    });
                }
            }
        }
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(GameEvent.NEXT_GAME_TRANSITION, this.handleNextGameTransition);
    }
    async handleNextGameTransition() {
        const promises = [];
        if (this.cellIndex >= 0) {
            promises.push(this.unhighlightHighlightedCell());
            for (let exceededIndex = 0; exceededIndex < this.cellIndex; ++exceededIndex) {
                promises.push(this.unhighlightExceededCell(exceededIndex));
            }
        }
        await Promise.all(promises);
    }
    async unhighlightHighlightedCell() {
        const [currentHighlightTrack, currentHighlightAnimation] = PayTableSpineRegister.getCurrentHighlightAnimation(this.cellIndex);
        this.customCells[this.cellIndex].setState("UNREACHED");
        await new Promise(resolve => {
            SpineUtil.play(this.animation, currentHighlightAnimation, resolve, null, currentHighlightTrack);
            GameSpineUtil.setReversed(this.animation, currentHighlightTrack, true);
        });
    }
    async unhighlightExceededCell(index) {
        const delay = (this.cellIndex - index) * 50 + 500;
        await new Promise(resolve => this.callAfter(delay, resolve));
        this.customCells[index].setState("UNREACHED", true);
        const [prizeExceededTrack, prizeExceededAnimation] = PayTableSpineRegister.getPreviousUnhighlightAnimation(index);
        await new Promise(resolve => {
            SpineUtil.play(this.animation, prizeExceededAnimation, resolve, null, prizeExceededTrack);
            GameSpineUtil.setReversed(this.animation, prizeExceededTrack, true);
        });
    }
    /**
     * Adjust the font size to linearly interpret between the "3 slingo" and "10 slingo" sizes
     * This is done to (try) create a clear diagonal in the paytable. This is awkward due to the paytable being diagonal itself
     * We create the diagonal from 3 slingo's -> 10 slingo's. With 3 and 10 being the min/max font sizes
     */
    handleFontSizingDynamic() {
        if (this.uiProperties.portraitPrimary)
            return;
        //Bottom cells dont have text, and the top one uses a different style due to being FH
        const fields = this.customCells.slice(fontCellsBottomToIgnore, this.customCells.length - fontCellsTopToIgnore);
        const minMax = GameMathUtil.toMinMax(fields, x => x.getFontSize());
        //The top and bottom cells (bottom below full house) we dont adjust due to them being the min and max
        for (let i = fontCellsBottomToIgnore + 1; i < this.customCells.length - fontCellsTopToIgnore - 1; i++) {
            const targetSize = GameMathUtil.lerp(minMax.min, minMax.max, (i - fontCellsBottomToIgnore) / (fields.length - 1));
            this.customCells[i].updateFontStyle(targetSize);
        }
    }
    updateCellOrientation() {
        const spine = this.animation.getSpine(this.renderer.getOrientation());
        this.customCells.forEach((cell, index) => cell.slotInSpine(spine, index));
    }
    numberOfPatternsToCell(numberOfPatterns) {
        return Math.min(numberOfPatterns, ApiConstants.CELL_COUNT) - 1;
    }
    handleAvailableWin() {
        const availableWin = this.model.getAvailableWin();
        const numberOfPatterns = availableWin.getNumberOfPatterns();
        const cellIndex = this.numberOfPatternsToCell(numberOfPatterns);
        const [availableWinTrack, availableWinAnimation] = PayTableSpineRegister.getAvailableWinAnimation(cellIndex);
        SpineUtil.play(this.animation, availableWinAnimation, null, null, availableWinTrack);
        GameSpineUtil.setReversed(this.animation, availableWinTrack, false);
        const onComplete = async () => {
            const durationSeconds = GameSpineUtil.getDuration(this.animation, availableWinAnimation);
            //We dont want to wait the full duration, only X%. Otherwise our text returns too quickly.
            await gsap.to({}, { duration: 0.8 * durationSeconds });
            this.customCells[cellIndex].clearAvailableWin();
        };
        this.customCells[cellIndex].showAvailableWin();
        onComplete();
    }
    handleSpinRequest() {
        if (this.model.isPurchaseSpin()) {
            this.handleAvailableWinClear();
        }
    }
    handleAvailableWinClear() {
        this.customCells.forEach(x => x.clearAvailableWin());
        for (const spine of GameSpineUtil.orientations(this.animation)) {
            for (let i = this.cellIndex + 1; i < ApiConstants.CELL_COUNT; ++i) {
                spine.state.clearTrack(i);
                spine.skeleton.setToSetupPose();
                this.customCells[i].setState("UNREACHED");
            }
        }
    }
    clearHighlight() {
        this.handleAvailableWinClear();
    }
    updatePrizeValues() {
        const payoutConfig = this.model.getPayoutConfig();
        payoutConfig
            .getPatternPayouts()
            .forEach((payoutMultiplier, payoutIndex) => {
            if (payoutMultiplier === 0) {
                return;
            }
            const cellIndex = this.numberOfPatternsToCell(payoutIndex + 1);
            const prizeValue = this.getPrizeValue(payoutMultiplier);
            this.customCells[cellIndex].setValue(prizeValue);
        });
        this.dispatchGame(SlingoEvent.PAY_TABLE_UPDATE);
    }
    getPrizeValue(payoutMultiplier) {
        return this.formatCurrency(payoutMultiplier * this.model.getCurrentStake());
    }
    updateCells(numberOfPatterns) {
        const previousCellIndex = this.cellIndex;
        this.cellIndex = this.numberOfPatternsToCell(numberOfPatterns);
        if (this.cellIndex === previousCellIndex) {
            return;
        }
        if (this.cellIndex >= 0) {
            // Show exceeded prizes.
            for (let exceededIndex = Math.max(0, previousCellIndex); exceededIndex < this.cellIndex; ++exceededIndex) {
                const [prizeExceededTrack, prizeExceededAnimation] = PayTableSpineRegister.getPrizeExceededAnimation(exceededIndex);
                SpineUtil.play(this.animation, prizeExceededAnimation, null, null, prizeExceededTrack);
                GameSpineUtil.setReversed(this.animation, prizeExceededTrack, false);
                this.customCells[exceededIndex].setState("EXCEEDED", true);
            }
            // Highlight the current cell while un-highlighting the previous cell.
            if (this.cellIndex > 0) {
                const [previousUnhighlightTrack, previousUnhighlightAnimation] = PayTableSpineRegister.getPreviousUnhighlightAnimation(this.cellIndex - 1);
                SpineUtil.play(this.animation, previousUnhighlightAnimation, null, null, previousUnhighlightTrack);
                GameSpineUtil.setReversed(this.animation, previousUnhighlightTrack, false);
            }
            const [currentHighlightTrack, currentHighlightAnimation] = PayTableSpineRegister.getCurrentHighlightAnimation(this.cellIndex);
            SpineUtil.play(this.animation, currentHighlightAnimation, null, null, currentHighlightTrack);
            GameSpineUtil.setReversed(this.animation, currentHighlightTrack, false);
            this.customCells[this.cellIndex].setState("HIGHLIGHTED");
        }
        // Show unreached prizes.
        for (let unreachedIndex = this.cellIndex + 1; unreachedIndex < ApiConstants.CELL_COUNT; ++unreachedIndex) {
            const [prizeUnreachedTrack, prizeUnreachedAnimation] = PayTableSpineRegister.getPrizeUnreachedAnimation(unreachedIndex);
            SpineUtil.play(this.animation, prizeUnreachedAnimation, null, null, prizeUnreachedTrack);
            GameSpineUtil.setReversed(this.animation, prizeUnreachedTrack, false);
            this.customCells[unreachedIndex].setState("UNREACHED");
        }
    }
    handleRotate(event) {
        super.handleRotate(event);
        const data = event?.getCurrentTarget() || {};
        this.visible = this.renderer.isLandscape(data.forceOrientation);
        if (this.backToGameButton) {
            this.backToGameButton.visible = this.renderer.isPortrait(data.forceOrientation);
        }
        this.updateCellOrientation();
    }
}
const fontCellsBottomToIgnore = 2;
const fontCellsTopToIgnore = 1;
//# sourceMappingURL=GamePayTable.js.map